import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider, createBrowserRouter, Navigate } from 'react-router-dom'
import './styles/index.css'
import { CompareProvider } from './context/CompareContext'
import AppShell from './components/AppShell'
import Landing from './pages/Landing'
import NotFound from './pages/NotFound'
import AuthCallback from './pages/AuthCallback'
import Onboarding from './pages/Onboarding'
import OnboardingAgent from './pages/OnboardingAgent'
import OnboardingCoach from './pages/OnboardingCoach'
import RepresentConfirm from './pages/RepresentConfirm'
import Dashboard from './pages/Dashboard'
import PlayerProfile from './pages/PlayerProfile'
import PlayerNew from './pages/PlayerNew'
import PlayerEdit from './pages/PlayerEdit'
import AgentEdit from './pages/AgentEdit'
import AgentProfile from './pages/AgentProfile'
import CoachProfile from './pages/CoachProfile'
import CoachEdit from './pages/CoachEdit'
import ReferenceNew from './pages/ReferenceNew'
import TeamProfile from './pages/TeamProfile'
import Search from './pages/Search'
import Discover from './pages/Discover'
import Compare from './pages/Compare'
import {
  BrowseIndex,
  BrowseCountry,
  BrowseLeague,
  BrowseRegion,
} from './pages/Browse'
import SavedPlayers from './pages/SavedPlayers'
import Messages from './pages/Messages'
import Billing from './pages/Billing'
import Settings from './pages/Settings'
import Pricing from './pages/Pricing'

const router = createBrowserRouter([
  { path: '/', element: <Landing /> },
  // /sign-in isn't a real page — sign-in is the Google OAuth flow on the
  // landing page. Redirect a direct hit (or a stale link) to the sign-in
  // surface instead of letting it fall through to the 404 catch-all.
  { path: '/sign-in', element: <Navigate to="/" replace /> },
  // M3 — bare /agents and /coaches have no directory page yet (only
  // /agents/:slug exists, matched inside AppShell below). Redirect the bare
  // paths to the player-browse surface instead of falling through to the 404
  // catch-all. Re-point when the agent/coach directories ship.
  { path: '/agents', element: <Navigate to="/discover" replace /> },
  { path: '/coaches', element: <Navigate to="/discover" replace /> },
  { path: '/auth/callback', element: <AuthCallback /> },
  { path: '/onboarding', element: <Onboarding /> },
  { path: '/onboarding/agent', element: <OnboardingAgent /> },
  { path: '/onboarding/coach', element: <OnboardingCoach /> },
  // Claim→Confirm — public representation confirm page (player opens from the
  // emailed link; no login). Standalone, outside AppShell — no authed-nav fetch.
  { path: '/represent/:token', element: <RepresentConfirm /> },
  {
    element: <AppShell />,
    // M6.2-followup-2 — errorElement catches runtime exceptions thrown
    // inside any AppShell child route. Without it, the default React
    // Router "Unexpected Application Error" page renders (developer-
    // facing scaffolding leaking to public). NotFound branches on
    // useRouteError to show 404 vs generic error copy.
    errorElement: <NotFound />,
    children: [
      { path: '/dashboard', element: <Dashboard /> },
      { path: '/dashboard/agent/edit', element: <AgentEdit /> },
      { path: '/dashboard/coach/edit', element: <CoachEdit /> },
      { path: '/dashboard/players/new', element: <PlayerNew /> },
      { path: '/dashboard/players/:slug/edit', element: <PlayerEdit /> },
      { path: '/players/:slug', element: <PlayerProfile /> },
      { path: '/players/:slug/reference/new', element: <ReferenceNew /> },
      { path: '/agents/:slug', element: <AgentProfile /> },
      { path: '/coaches/:slug', element: <CoachProfile /> },
      { path: '/teams/:slug', element: <TeamProfile /> },
      { path: '/discover', element: <Discover /> },
      { path: '/compare', element: <Compare /> },
      { path: '/search', element: <Search /> },
      { path: '/browse', element: <BrowseIndex /> },
      { path: '/browse/region/:slug', element: <BrowseRegion /> },
      { path: '/browse/country/:slug', element: <BrowseCountry /> },
      { path: '/browse/league/:slug', element: <BrowseLeague /> },
      { path: '/saved', element: <SavedPlayers /> },
      { path: '/messages', element: <Messages /> },
      { path: '/billing', element: <Billing /> },
      { path: '/settings', element: <Settings /> },
      { path: '/pricing', element: <Pricing /> },
    ],
  },
  // M6.2-followup-2 — catch-all for unmatched top-level URLs (e.g.
  // /sign-in, which isn't a real route — sign-in is a Google OAuth
  // modal). Sits at the end so it only fires when no other route
  // matches. Outside AppShell so 404s render standalone (no TopNav
  // fetch on a dead-end).
  { path: '*', element: <NotFound /> },
])

const root = document.getElementById('root')
if (!root) throw new Error('Root element not found')

createRoot(root).render(
  <StrictMode>
    <CompareProvider>
      <RouterProvider router={router} />
    </CompareProvider>
  </StrictMode>,
)
